import { useEffect, useMemo, useState } from 'react';
import type React from 'react';
import { Shield } from 'lucide-react';

type StandingRow = Record<string, any>;
type StandingGroup = {
  name?: string;
  stage?: string;
  rows?: StandingRow[];
};
type StandingsData = {
  standings?: StandingRow[];
  groupStats?: { groups?: StandingGroup[] };
};

function TeamMark({ team, size = 40 }: { team: StandingRow; size?: number }) {
  const label = team.shortName || team.short || team.name || team.teamName || '?';
  const [imageFailed, setImageFailed] = useState(false);
  if (team.logoUrl && !imageFailed) return <img className="team-mark" src={team.logoUrl} alt="" style={{ width: size, height: size }} onError={() => setImageFailed(true)} />;
  return <span className="team-mark team-mark--fallback" style={{ width: size, height: size }}>{String(label).slice(0, 1)}</span>;
}

function FormBadges({ value }: { value?: unknown }) {
  const items = String(value || '').split('-').map((item) => item.trim()).filter(Boolean);
  if (!items.length) return <span className="form-badges form-badges--empty">—</span>;
  return <span className="form-badges">{items.slice(0, 5).map((item, index) => <span className={`form-badge form-badge--${item.toLowerCase()}`} key={`${item}-${index}`}>{item}</span>)}</span>;
}

function EmptyState({ title, text }: { title: string; text: string }) {
  return <div className="empty-state"><Shield size={30} /><h3>{title}</h3><p>{text}</p></div>;
}

function Eyebrow({ children }: { children: string }) {
  return <span className="eyebrow">{children}</span>;
}

function SectionTitle({ eyebrow, title, action }: { eyebrow: string; title: string; action?: React.ReactNode }) {
  return <div className="section-title"><div><Eyebrow>{eyebrow}</Eyebrow><h2>{title}</h2></div>{action}</div>;
}

function StandingHeading({ title, stage, action }: { title: string; stage?: string; action?: React.ReactNode }) {
  return (
    <div className="standing-heading">
      <h2>{title}</h2>
      {stage ? <em>{stage}</em> : null}
      {action}
    </div>
  );
}

function groupKey(group: StandingGroup) {
  return `${group.stage || 'stage'}-${group.name || 'group'}`;
}

export function Standings({ rows, linked = true }: { rows: StandingRow[]; linked?: boolean }) {
  if (!rows.length) return <EmptyState title="Standings are waiting" text="The table updates when results arrive." />;
  return (
    <div className="standings">
      <div className="standings__head"><span>Team</span><span>M</span><span>W</span><span>L</span><span>NR</span><span>Pts</span><span>NRR</span><span>Last 5</span></div>
      {rows.map((row, index) => {
        const rank = row.groupRank || row.rank || index + 1;
        const label = row.shortName || row.short || row.name || row.teamName || 'Team';
        const teamContent = linked && row.teamId
          ? <a className="standings__team standings__team--link" href={`#teams/${encodeURIComponent(row.teamId)}`}><b>{rank}</b><TeamMark team={row} size={30} /><span>{label}</span></a>
          : <span className="standings__team"><b>{rank}</b><TeamMark team={row} size={30} /><span>{label}</span></span>;
        return (
          <div className="standings__row" key={row.teamId || row.id || row.name || row.teamName || index}>
            {teamContent}
            <span>{row.played ?? 0}</span>
            <span>{row.won ?? 0}</span>
            <span>{row.lost ?? 0}</span>
            <span>{row.noResult ?? 0}</span>
            <strong>{row.points ?? 0}</strong>
            <span className={(Number(row.nrr) || 0) >= 0 ? 'positive' : 'negative'}>{row.nrr ?? '—'}</span>
            <span><FormBadges value={row.last5} /></span>
          </div>
        );
      })}
    </div>
  );
}

export function GroupedStandings({ data, selectedTeamId, linked = true }: { data: StandingsData; selectedTeamId?: string; linked?: boolean }) {
  const groups = useMemo(() => {
    const sorted = [...(data.groupStats?.groups || [])].sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
    if (!selectedTeamId) return sorted;
    return sorted.map((group) => ({
      ...group,
      rows: (group.rows || []).filter((row) => row.teamId === selectedTeamId || row.id === selectedTeamId),
    })).filter((group) => group.rows?.length);
  }, [data.groupStats?.groups, selectedTeamId]);
  const [activeGroupKey, setActiveGroupKey] = useState('');
  useEffect(() => {
    if (!groups.length) return;
    const keys = groups.map(groupKey);
    if (!activeGroupKey || !keys.includes(activeGroupKey)) setActiveGroupKey(keys[0]);
  }, [activeGroupKey, groups]);
  if (!groups.length) {
    const rows = selectedTeamId
      ? (data.standings || []).filter((row) => row.teamId === selectedTeamId || row.id === selectedTeamId)
      : data.standings || [];
    return <Standings rows={rows} linked={linked} />;
  }
  const resolvedActiveGroupKey = activeGroupKey || groupKey(groups[0]);
  const activeGroup = groups.find((group) => groupKey(group) === resolvedActiveGroupKey) || groups[0];
  const groupSwitch = groups.length > 1 ? (
    <div className="segmented group-switch" role="tablist" aria-label="Standings group">
      {groups.map((group) => {
        const key = groupKey(group);
        return <button type="button" role="tab" aria-selected={key === resolvedActiveGroupKey} className={key === resolvedActiveGroupKey ? 'active' : ''} key={key} onClick={() => setActiveGroupKey(key)}>{group.name || 'Group'}</button>;
      })}
    </div>
  ) : null;
  return <div className="group-standings">
    <section className="standing-group standing-group--mobile" key={`${groupKey(activeGroup)}-mobile`}>
      <StandingHeading title={activeGroup.name || 'Group'} stage={activeGroup.stage || 'League matches'} action={groupSwitch} />
      <Standings rows={activeGroup.rows || []} linked={linked} />
    </section>
    {groups.map((group) => (
      <section className="standing-group standing-group--desktop" key={groupKey(group)}>
        <StandingHeading title={group.name || 'Group'} stage={group.stage || 'League matches'} />
        <Standings rows={group.rows || []} linked={linked} />
      </section>
    ))}
  </div>;
}
